import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {
    getDailyQuestInstance,
    overrideDailyQuestTaskProgress,
    resolveStrapiMediaUrlForDisplay,
    resyncDailyQuestInstance,
} from '../Api';
import DailyQuestAdminInstanceDto, { DailyQuestAssignedTaskDto } from '../models/dtos/DailyQuestAdminInstanceDto';
import { DailyQuestSourceChip, DailyQuestStatusChip, isIgnoredOffer } from './DailyQuestChips';

function title(quest: DailyQuestAdminInstanceDto): string {
    return quest.titleLocalizations.en || quest.templateName;
}

function englishText(value: Record<string, string> | undefined, fallback: string): string {
    return value?.en?.trim() || fallback;
}

/** Ace offers carry their tasks before any api user task exists (userTaskId 0). */
function hasUserTask(task: DailyQuestAssignedTaskDto): boolean {
    return task.userTaskId > 0;
}

function TaskImplementationDot({ implemented }: { implemented: boolean }) {
    return (
        <Box
            component="span"
            title={implemented ? 'Implemented in v7' : 'Not implemented in v7'}
            sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: implemented ? 'success.main' : 'grey.400',
                display: 'inline-block',
                flex: '0 0 auto',
            }}
        />
    );
}

export default function DailyQuestDetailsDialog({
    questId,
    open,
    onClose,
    onChanged,
}: {
    questId: string | null;
    open: boolean;
    onClose: () => void;
    onChanged?: () => void;
}) {
    const [quest, setQuest] = React.useState<DailyQuestAdminInstanceDto | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [busyTaskId, setBusyTaskId] = React.useState<number | null>(null);
    const [message, setMessage] = React.useState<string | null>(null);

    const load = React.useCallback(async () => {
        if (!questId || !open) return;
        setLoading(true);
        setMessage(null);
        const item = await getDailyQuestInstance(questId);
        setQuest(item);
        setLoading(false);
    }, [open, questId]);

    React.useEffect(() => {
        void load();
    }, [load]);

    const applyUpdatedQuest = (item: DailyQuestAdminInstanceDto | null, fallbackError: string) => {
        if (!item) {
            setMessage(fallbackError);
            return;
        }
        setQuest(item);
        onChanged?.();
    };

    const resync = async () => {
        if (!questId) return;
        setLoading(true);
        const result = await resyncDailyQuestInstance(questId);
        setLoading(false);
        if (result.error) {
            setMessage(result.error);
            return;
        }
        applyUpdatedQuest(result.item, 'Daily quest was not re-synced.');
    };

    const overrideProgress = async (task: DailyQuestAssignedTaskDto, action: 'RESET' | 'COMPLETE') => {
        if (!questId) return;
        setBusyTaskId(task.userTaskId);
        const result = await overrideDailyQuestTaskProgress(questId, task.userTaskId, action);
        setBusyTaskId(null);
        if (result.error) {
            setMessage(result.error);
            return;
        }
        applyUpdatedQuest(result.item, 'Task progress was not updated.');
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>{quest ? title(quest) : 'Daily quest'}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ pt: 1 }}>
                    {loading && <LinearProgress />}
                    {message && <Alert severity="error" onClose={() => setMessage(null)}>{message}</Alert>}
                    {quest && (
                        <>
                            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                                <DailyQuestStatusChip quest={quest} prefix="Status: " />
                                <DailyQuestSourceChip quest={quest} />
                                {quest.offeredAt && <Chip size="small" label={`Offered: ${new Date(quest.offeredAt).toLocaleString()}`} />}
                                {quest.answeredAt && <Chip size="small" label={`Answered: ${new Date(quest.answeredAt).toLocaleString()}`} />}
                                {isIgnoredOffer(quest) && <Chip size="small" label="Answered: never (lapsed)" />}
                                <Chip size="small" label={`Reward: ${quest.rewardBetcoins} betcoins`} />
                                <Chip size="small" label={quest.rewardClaimed ? 'Reward claimed' : 'Reward not claimed'} color={quest.rewardClaimed ? 'success' : 'default'} />
                                {quest.claimedAt && <Chip size="small" label={`Claimed: ${new Date(quest.claimedAt).toLocaleString()}`} />}
                                <Chip
                                    size="small"
                                    label={`${quest.status === 'OFFERED' ? 'Offer lapses' : 'Expires'}: ${new Date(quest.expiresAt).toLocaleString()}`}
                                />
                            </Stack>
                            {quest.coverImageUrl && (
                                <Box
                                    component="img"
                                    src={resolveStrapiMediaUrlForDisplay(quest.coverImageUrl) ?? quest.coverImageUrl}
                                    alt=""
                                    sx={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                                />
                            )}
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell />
                                        <TableCell>Task</TableCell>
                                        <TableCell>User task ID</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Progress</TableCell>
                                        <TableCell>External ID</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {quest.tasks.map((task, index) => (
                                        <TableRow key={`${task.userTaskId}-${task.poolEntryId}-${index}`}>
                                            <TableCell width={56}>
                                                {task.iconUrl && (
                                                    <Box
                                                        component="img"
                                                        src={resolveStrapiMediaUrlForDisplay(task.iconUrl) ?? task.iconUrl}
                                                        alt=""
                                                        sx={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Stack spacing={0.25}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <TaskImplementationDot implemented={task.userTaskTemplateImplemented} />
                                                        <Typography variant="body2">{englishText(task.nameLocalizations, task.taskType)}</Typography>
                                                    </Stack>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {englishText(task.descriptionLocalizations, task.taskType)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {task.taskType}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>{hasUserTask(task) ? task.userTaskId : '-'}</TableCell>
                                            <TableCell>{task.status}</TableCell>
                                            <TableCell>{task.progressCurrent} / {task.progressTarget}</TableCell>
                                            <TableCell>{task.userTaskExternalId || '-'}</TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" gap={1} justifyContent="flex-end">
                                                    <Button
                                                        size="small"
                                                        disabled={!hasUserTask(task) || busyTaskId === task.userTaskId || task.progressCurrent <= 0}
                                                        onClick={() => void overrideProgress(task, 'RESET')}
                                                    >
                                                        Reset to 0
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        disabled={!hasUserTask(task) || busyTaskId === task.userTaskId || task.status === 'COMPLETED'}
                                                        onClick={() => void overrideProgress(task, 'COMPLETE')}
                                                    >
                                                        Mark completed
                                                    </Button>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={resync} disabled={!questId || loading}>Re-sync</Button>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
